import { useFormik } from 'formik';
import { capitalize, startCase, camelCase } from 'lodash';
import React, { Fragment, useState } from 'react';
import { useSelector } from 'react-redux';
import { Col, Row } from 'reactstrap';
import * as Yup from 'yup';
import PhoneInput from 'react-phone-input-2';
import { OTHER_OPTION } from '../../../helpers/constants';

const validationSchema = Yup.object({
  category: Yup.number().required('Please select category'),
  date_of_arrival: Yup.date().required('Please select date of arrival'),
  type: Yup.string()
    .nullable()
    .when('category', (category, schema) => {
      if (category == 1) {  // Changed from '1' to 1 to match number type
        return schema.required('Please select type');
      }
      return schema.notRequired();
    }),
  single_item_title: Yup.string().when('category', (category, schema) => {
    if (category != 1) {
      return schema.required('Please enter item name');
    }
    return schema.notRequired();
  }),
  clearing_discharge_port: Yup.string().required('Please select port of discharge'), // Changed field name to match backend requirement
  email: Yup.string()
    .email('Please enter valid email')
    .required('Please enter email'),
  phone: Yup.string(),
});

const ClearingServiceTab = ({ onSubmit }) => {
  const serviceData = useSelector((state) => state.enumData);
  const [token, setToken] = useState(null);
  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    touched,
    resetForm,
    setFieldError,
    dirty,
    isValid,
    setFieldValue,
  } = useFormik({
    initialValues: {
      category: 1,  // Changed from '1' to 1 to match number type
      itemOrVehicle: '',
      clearing_discharge_port: '', // Changed field name to match backend requirement
      terminal_uuid: '',
      terminal_other: '',
      shipping_line_uuid: '',
      shipping_line_other: '',
      date_of_arrival: '',
      container_size: '',
      pickup_location: '',
      destination: '',
      email: '',
      phone: '',
      type: '',  // Changed from null to empty string
      single_item_title: '',
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      // "Others" is a form-only sentinel; the API takes a uuid or a typed name
      // on each side, never both. Cleared here so a guest who picks Others,
      // types a name, then changes back to a real terminal does not send both.
      if (values.terminal_uuid === OTHER_OPTION) {
        values.terminal_uuid = '';
      } else {
        values.terminal_other = '';
      }
      if (values.shipping_line_uuid === OTHER_OPTION) {
        values.shipping_line_uuid = '';
      } else {
        values.shipping_line_other = '';
      }

      // Ensure job_type is always set to 'clearing'
      values.job_type = 'clearing';
      onSubmit(values, resetForm, (errors) => {
        if (errors) {
          Object.keys(errors).forEach((key) => {
            setFieldError(key, errors[key][0]);
          });
        }
      });
    },
  });

  const handleVerify = (newToken) => {
    setToken(newToken);
  };

  console.log(errors);
  return (
    <form onSubmit={handleSubmit} id="service-form">
      <Row>
        <Col md={6} lg={3} className="mt">
          <label style={{ marginBottom: '0px' }}>Select Category</label>
          <select
            name="category"
            className="form-control"
            onChange={(e) => {
              handleChange(e);
            }}
            value={values.category}
          >
            {Object.keys(serviceData.job.category).map((key) => (
              <option value={key} key={key}>
                {capitalize(serviceData.job.category[key])}
              </option>
            ))}
          </select>
          <span className="text-danger">{touched.category && errors.category}</span>
        </Col>

        {values.category == 1 && (  // Changed from === '1' to == 1
          <Col md={6} lg={3} className="mt">
            <label style={{ marginBottom: '0px' }}>Select Type</label>
            <select
              name="type"
              className="form-control"
              onChange={handleChange}
              value={values.type}
            >
              <option value="">Select Type</option>
              {Object.keys(serviceData.job.vehicle_type).map((key) => (
                <Fragment key={key}>
                  {key !== '3' && (
                    <option value={key} key={key}>
                      {capitalize(serviceData.job.vehicle_type[key])}
                    </option>
                  )}
                </Fragment>
              ))}
            </select>

            <span className="text-danger">{touched.type && errors.type}</span>
          </Col>
        )}

        {(values.category == 2 || values.category == 3) && (  // Changed from === '2' to == 2
          <Col md={6} lg={3} className="mt">
            <label style={{ marginBottom: '0px' }}>Item Name {'(Separated by comma) '}</label>
            <input
              name="single_item_title"
              type="text"
              className="form-control"
              placeholder="item 1, item 2"
              onChange={handleChange}
              value={values.single_item_title}
            />
            <span className="text-danger">{touched.single_item_title && errors.single_item_title}</span>
          </Col>
        )}

        <Col md={6} lg={3} className="mt">
          <label style={{ marginBottom: '0px' }}>Port of Discharge</label>
          <select
            name="clearing_discharge_port" // Changed field name to match backend requirement
            type="text"
            className="form-control"
            onChange={handleChange}
            value={values.clearing_discharge_port} // Changed field name to match backend requirement
          >
            <option value="">Select Port</option>
            {(serviceData?.active_ports || serviceData?.ports)?.length > 0 &&
              (serviceData?.active_ports || serviceData?.ports).map((obj) => (
                <option value={obj.uuid} key={obj.uuid}>
                  {/* Rendered as stored: startCase(camelCase()) turned "Ogun II"
                      into "Ogun Ii", "Abuja (FCT)" into "Abuja Fct" and
                      "PTML" into "Ptml". */}
                  {obj.port_of_operation}
                </option>
              ))}
          </select>
          <span className="text-danger">
            {touched.clearing_discharge_port && errors.clearing_discharge_port}
          </span>
        </Col>

        {/* Optional here on purpose — the public form asks for as little as
            possible. The operator still cannot approve the job for bidding
            until both are set, so nothing reaches agents unspecified.
            Terminals are filtered to the chosen port. */}
        <Col md={6} lg={3} className="mt">
          <label style={{ marginBottom: '0px' }}>Terminal</label>
          <select
            name="terminal_uuid"
            className="form-control"
            onChange={handleChange}
            value={values.terminal_uuid}
          >
            <option value="">
              {values.clearing_discharge_port ? 'Select Terminal' : 'Select a port first'}
            </option>
            {serviceData?.terminals?.length > 0 &&
              serviceData.terminals
                .filter(
                  (t) =>
                    !values.clearing_discharge_port ||
                    t.port_uuid === values.clearing_discharge_port,
                )
                .map((t) => (
                  <option value={t.uuid} key={t.uuid}>
                    {t.terminal_name}
                  </option>
                ))}
            <option value={OTHER_OPTION}>Others (type it in)</option>
          </select>
        </Col>

        {/* Its own column rather than tucked under the select above, so it
            takes a grid slot like any other field and the row stays aligned. */}
        {values.terminal_uuid === OTHER_OPTION && (
          <Col md={6} lg={3} className="mt">
            <label style={{ marginBottom: '0px' }}>Terminal Name</label>
            <input
              name="terminal_other"
              type="text"
              className="form-control"
              maxLength={120}
              placeholder="Enter the terminal name"
              onChange={handleChange}
              value={values.terminal_other}
            />
            <span className="text-danger">{touched.terminal_other && errors.terminal_other}</span>
          </Col>
        )}

        <Col md={6} lg={3} className="mt">
          <label style={{ marginBottom: '0px' }}>Shipping Line</label>
          <select
            name="shipping_line_uuid"
            className="form-control"
            onChange={handleChange}
            value={values.shipping_line_uuid}
          >
            <option value="">Select Shipping Line</option>
            {serviceData?.shipping_lines?.length > 0 &&
              serviceData.shipping_lines.map((s) => (
                <option value={s.uuid} key={s.uuid}>
                  {s.shipping_line_name}
                </option>
              ))}
            <option value={OTHER_OPTION}>Others (type it in)</option>
          </select>
        </Col>

        {values.shipping_line_uuid === OTHER_OPTION && (
          <Col md={6} lg={3} className="mt">
            <label style={{ marginBottom: '0px' }}>Shipping Line Name</label>
            <input
              name="shipping_line_other"
              type="text"
              className="form-control"
              maxLength={120}
              placeholder="Enter the shipping line name"
              onChange={handleChange}
              value={values.shipping_line_other}
            />
            <span className="text-danger">
              {touched.shipping_line_other && errors.shipping_line_other}
            </span>
          </Col>
        )}

        <Col md={6} lg={3} className="mt">
          <label style={{ marginBottom: '0px' }}>Expected Date of Arrival</label>
          <input
            name="date_of_arrival"
            type="date"
            className="form-control"
            onChange={handleChange}
            value={values.date_of_arrival}
            min={new Date().toISOString().split('T')[0]}
          />
          <span className="text-danger">{touched.date_of_arrival && errors.date_of_arrival}</span>
        </Col>

        <Col md={6} lg={4} className="mt">
          <label style={{ marginBottom: '0px' }}>Email</label>
          <input
            name="email"
            type="email"
            className="form-control"
            placeholder="Enter your Email"
            onChange={handleChange}
            value={values.email}
          />
          <span className="text-danger">{touched.email && errors.email}</span>
        </Col>

        <Col md={6} lg={4} className="mt position-relative">
          <label style={{ marginBottom: '0px' }}>Phone</label>
          <PhoneInput
            country={'ng'}
            value={values.phone}
            onChange={(x) => setFieldValue('phone', '+' + x)}
            placeholder="Enter your Phone"
            inputStyle={{
              height: '50px',
              width: '100%',
            }}
          />
        </Col>
        <Col md={6} lg={4} className="mt">
          <div className="text-right">
            <button className="post-button w-100" type="submit" disabled={!(isValid && dirty)}>
              Submit
            </button>
          </div>
        </Col>
      </Row>
    </form>
  );
};

export default ClearingServiceTab;